// booking_page.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import moment from "moment";
import { RingLoader, BarLoader } from "react-spinners";
import './booking_page.css'

export default function BookingPage(props) {
  const {
    fetchAvailableDates,
    availability,
    fetchAvailableTimes,
    times,
    client, // clicked APPOINTMENT object from Frontdesk
    closeTouchup,
    postAppointment,
    newAppointment,
    fetchAppointments3,
    monthlyAppointments2,
    fetchArtist,
    artist,
  } = props;

  /* =========================
     BASE APPT (clicked) + recommended window
     ========================= */

  const parseApptDate = useCallback((appt) => {
    const raw = appt?.date;
    const candidates = [
      moment(raw, "MMM D YYYY", true),
      moment(raw, "MMMM D YYYY", true),
      moment(raw, "YYYY-MM-DD", true),
      moment(raw), // fallback
    ];
    const m = candidates.find((x) => x && x.isValid());
    return m && m.isValid() ? m : moment();
  }, []);

  const baseApptMoment = useMemo(() => parseApptDate(client), [client, parseApptDate]);

  // recommended 6–8 weeks AFTER the clicked appointment
  const recStart = useMemo(() => baseApptMoment.clone().add(6, "weeks").startOf("day"), [baseApptMoment]);
  const recEnd = useMemo(() => baseApptMoment.clone().add(8, "weeks").endOf("day"), [baseApptMoment]);

  /* =========================
     VIEW STATE
     ========================= */

  const [viewYear, setViewYear] = useState(recStart.year());
  const [viewMonthIndex, setViewMonthIndex] = useState(recStart.month()); // 0-11
  const [currentDay, setCurrentDay] = useState(recStart.date());

  // touch-up selection
  const [selectedApptID, setSelectedApptID] = useState(null);
  const [TUDate, setTUDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [notes, setNotes] = useState("");

  // NEW: time UX
  const [showTimes, setShowTimes] = useState(true);

  // UI
  const [monthsOpen, setMonthsOpen] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [loadingDates, setLoadingDates] = useState(false);

  // available day numbers + times
  const [availabilityArray, setAvailabilityArray] = useState([]);
  const [timesArray, setTimesArray] = useState([]);

  /* =========================
     Derived
     ========================= */

  const monthsFull = useMemo(
    () => [
      "January","February","March","April","May","June",
      "July","August","September","October","November","December"
    ],
    []
  );

  const monthDigit = useMemo(() => String(viewMonthIndex + 1).padStart(2, "0"), [viewMonthIndex]);
  const writtenMonth = useMemo(() => monthsFull[viewMonthIndex], [monthsFull, viewMonthIndex]);
  const writtenDate = useMemo(() => `${writtenMonth} ${viewYear}`, [writtenMonth, viewYear]);
  const viewMonthKey = useMemo(() => `${viewYear}-${monthDigit}`, [viewYear, monthDigit]);

  const weekdayshortname = useMemo(() => {
    return moment.weekdaysShort().map((day) => (
      <th key={day} className="week-day">
        {day}
      </th>
    ));
  }, []);

  const viewDateObject = useMemo(
    () => moment().set({ year: viewYear, month: viewMonthIndex, date: 1 }),
    [viewYear, viewMonthIndex]
  );

  /* =========================
     Init: when clicked appt changes
     ========================= */

  useEffect(() => {
    const nextStart = baseApptMoment.clone().add(6, "weeks");
    setViewYear(nextStart.year());
    setViewMonthIndex(nextStart.month());
    setCurrentDay(nextStart.date());

    // clear selection for new clicked appt
    setSelectedApptID(null);
    setTUDate(null);
    setSelectedTime(null);
    setNotes("");
    setAvailabilityArray([]);
    setTimesArray([]);
    setConfirmed(false);
    setMonthsOpen(false);
    setShowTimes(true);
  }, [baseApptMoment]);

  /* =========================
     Fetch artist metadata (TUIDs)
     ========================= */

  useEffect(() => {
    if (client?.calendarID) fetchArtist(client.calendarID);
  }, [client, fetchArtist]);

  /* =========================
     Availability + booked appts
     ========================= */

  const createAvailableArray = useCallback((avail) => {
    const arr = [];
    Object.keys(avail || {}).forEach((key) => {
      const d = String(avail[key]?.date || "");
      const parts = d.split("-");
      const day = parts?.[2] ? parts[2].replace(/^0+/, "") : null;
      if (day) arr.push(day);
    });
    return arr;
  }, []);

  const fetchMonthData = useCallback(async () => {
    if (!client?.calendarID || !selectedApptID) return;

    setLoadingDates(true);

    // booked appts for month (right calendar conflict preview)
    fetchAppointments3(client.calendarID, `${viewYear}-${monthDigit}-01`, `${viewYear}-${monthDigit}-31`);

    // available dates for selected touchup type
    fetchAvailableDates(client.calendarID, selectedApptID, viewMonthKey);

    setLoadingDates(false);
  }, [client, selectedApptID, fetchAppointments3, fetchAvailableDates, viewYear, monthDigit, viewMonthKey]);

  useEffect(() => {
    if (selectedApptID) fetchMonthData();
  }, [selectedApptID, viewMonthKey, fetchMonthData]);

  useEffect(() => {
    if (availability) setAvailabilityArray(createAvailableArray(availability));
    else setAvailabilityArray([]);
  }, [availability, createAvailableArray]);

  /* =========================
     Times
     ========================= */

  const setStandardTime = (timeHHMM) => {
    const parts = String(timeHHMM).split(":");
    const hours = parseInt(parts[0], 10);
    const minutes = parts[1];

    const h = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
    const suffix = hours > 11 ? " P.M." : " A.M.";
    const mm = String(minutes).padStart(2, "0");
    return `${h}:${mm}${suffix}`;
  };

  const convertTime12to24 = (time) => {
    const t = String(time);
    const isPM = t.includes("P.M.");
    const [hh, rest] = t.split(":");
    const mm = rest.replace(" A.M.", "").replace(" P.M.", "").trim();

    let hour = parseInt(hh, 10);
    if (isPM && hour !== 12) hour += 12;
    if (!isPM && hour === 12) hour = 0;

    return `${String(hour).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
  };

  const getTimes = useCallback(() => {
    if (!client?.calendarID || !selectedApptID || !TUDate) return;
    const dateStr = `${viewYear}-${monthDigit}-${String(TUDate).padStart(2, "0")}`;
    fetchAvailableTimes(client.calendarID, selectedApptID, dateStr);
  }, [client, selectedApptID, TUDate, viewYear, monthDigit, fetchAvailableTimes]);

  useEffect(() => {
    if (TUDate) getTimes();
  }, [TUDate, getTimes]);

  useEffect(() => {
    if (!times) {
      setTimesArray([]);
      return;
    }
    const arr = [];
    Object.keys(times).forEach((key) => {
      const iso = times[key]?.time; // "2026-01-01T10:30:00..."
      if (!iso) return;
      const hhmm = iso.split("T")[1]?.slice(0, 5);
      if (!hhmm) return;
      arr.push(setStandardTime(hhmm));
    });
    setTimesArray(arr);
  }, [times]);

  /* =========================
     Create appointment
     ========================= */

  const createAppointment = useCallback(() => {
    if (!selectedApptID || !selectedTime || !TUDate) return;

    const dateStr = `${viewYear}-${monthDigit}-${String(TUDate).padStart(2, "0")}T${convertTime12to24(selectedTime)}`;

    const apptDetails = {
      dt: dateStr,
      appointmentTypeID: selectedApptID,
      firstName: client?.firstName,
      lastName: client?.lastName,
      email: client?.email,
      calendarID: client?.calendarID,
      phone: client?.phone,
      note: notes || "",
    };

    postAppointment(apptDetails);
  }, [selectedApptID, selectedTime, TUDate, viewYear, monthDigit, client, notes, postAppointment]);

  useEffect(() => {
    if (newAppointment) {
      setConfirmed(true);
      setTimeout(() => window.location.reload(), 200);
    }
  }, [newAppointment]);

  /* =========================
     Month dropdown: upcoming months
     ========================= */

  const upcomingMonths = useMemo(() => {
    const list = [];
    const start = moment().set({ year: viewYear, month: viewMonthIndex, date: 1 });
    for (let i = 0; i < 10; i++) {
      const m = start.clone().add(i, "months");
      list.push({
        key: m.format("YYYY-MM"),
        label: m.format("MMMM YYYY"),
        year: m.year(),
        monthIndex: m.month(),
      });
    }
    return list;
  }, [viewYear, viewMonthIndex]);

  const setSelectedMonthYear = (y, mIndex) => {
    setViewYear(y);
    setViewMonthIndex(mIndex);
    setCurrentDay(1);

    // clear selections that depend on month
    setTUDate(null);
    setSelectedTime(null);
    setTimesArray([]);
    setAvailabilityArray([]);
    setMonthsOpen(false);
    setShowTimes(true);
  };

  /* =========================
     Calendar grid helpers
     ========================= */

  const firstDayOfMonth = useCallback(
    () => Number(viewDateObject.clone().startOf("month").format("d")),
    [viewDateObject]
  );

  const isInRecommendedWindow = (d) => {
    const m = moment(`${viewYear}-${monthDigit}-${String(d).padStart(2, "0")}`, "YYYY-MM-DD", true);
    return m.isBetween(recStart, recEnd, "day", "[]");
  };

  const isAvailable = (d) => availabilityArray.includes(String(d));

  const onSelectTUDate = (d) => {
    if (!isAvailable(d)) return;
    setTUDate(d);
    setSelectedTime(null);
    setTimesArray([]);
    setShowTimes(true);
  };

  // blanks
  const blanks = [];
  for (let i = 0; i < firstDayOfMonth(); i++) {
    blanks.push(
      <td key={`blank-${i}`} className="calendar-day empty">
        {""}
      </td>
    );
  }

  // left availability calendar
  const daysInMonth = [];
  for (let d = 1; d <= viewDateObject.daysInMonth(); d++) {
    const currentday = d === currentDay ? "today" : d === TUDate && isAvailable(d) ? "tudate" : "";
    const rangeClass = isInRecommendedWindow(d) ? "range" : "";

    daysInMonth.push(
      <td
        key={d}
        onClick={() => onSelectTUDate(d)}
        className={
          isAvailable(d)
            ? `calendar-day2 ${currentday} ${rangeClass} available`
            : `calendar-day2 ${currentday} ${rangeClass} not-available`
        }
      >
        {d}
      </td>
    );
  }

  const totalSlots = [...blanks, ...daysInMonth];
  const calRows = [];
  let cells = [];

  totalSlots.forEach((cell, i) => {
    if (i % 7 !== 0) cells.push(cell);
    else {
      calRows.push(cells);
      cells = [cell];
    }
    if (i === totalSlots.length - 1) calRows.push(cells);
  });

  const calendar = calRows.map((row, i) => <tr key={`row-${i}`}>{row}</tr>);

  /* =========================
     Right calendar: booked appts
     IMPORTANT: chips should show CLIENT NAME (not appt type)
     ========================= */

  const daysInMonthBooked = [];
  for (let d = 1; d <= viewDateObject.daysInMonth(); d++) {
    const currentday = d === currentDay ? "today" : d === TUDate && isAvailable(d) ? "tudate" : "";
    const rangeClass = isInRecommendedWindow(d) ? "range" : "";

    daysInMonthBooked.push(
      <td
        key={`b-${d}`}
        className={`calendar-day3 ${currentday} ${rangeClass} ${isAvailable(d) ? "available" : "not-available"}`}
      >
        {d}
        <div>
          {selectedApptID ? (
            monthlyAppointments2 ? (
              Object.keys(monthlyAppointments2)
                .reverse()
                .map((key) => {
                  const ap = monthlyAppointments2[key];
                  if (!ap?.date) return null;

                  const apMonth = ap.date.split(" ")[0]; // "Jan"
                  const apDay = parseInt(ap.date.split(" ")[1], 10);

                  const viewMonthShort = moment().set({ month: viewMonthIndex }).format("MMM");
                  if (!apMonth.includes(viewMonthShort) || apDay !== d) return null;

                  const cls =
                    ap.type?.includes("PACKAGE")
                      ? "package"
                      : ap.type?.includes("Brows")
                      ? "brows"
                      : ap.type?.includes("Lips")
                      ? "lips"
                      : ap.category?.includes("STUDENTS")
                      ? "students"
                      : ap.type?.includes("Lash")
                      ? "eyeliner"
                      : "other";

                  // ✅ SHOW CLIENT NAME (instead of appt type)
                  const clientName = `${ap.firstName || ""} ${ap.lastName || ""}`.trim();

                  return (
                <div key={ap._id || key} className={`appointment-box3 ${cls}`}>
  <div className="appt-client">{clientName || "Client"}</div>
  <div className="appt-time">{ap.time} – {ap.endTime}</div>
</div>

                  );
                })
            ) : (
              <BarLoader className="ring" color="#fff" size={30} />
            )
          ) : null}
        </div>
      </td>
    );
  }

  const totalSlotsBooked = [...blanks, ...daysInMonthBooked];
  const bookedRows = [];
  let bookedCells = [];

  totalSlotsBooked.forEach((cell, i) => {
    if (i % 7 !== 0) bookedCells.push(cell);
    else {
      bookedRows.push(bookedCells);
      bookedCells = [cell];
    }
    if (i === totalSlotsBooked.length - 1) bookedRows.push(bookedCells);
  });

  const calendarBooked = bookedRows.map((row, i) => <tr key={`brow-${i}`}>{row}</tr>);

  /* =========================
     UI actions
     ========================= */

  const jumpToRecommended = () => {
    setViewYear(recStart.year());
    setViewMonthIndex(recStart.month());
    setCurrentDay(recStart.date());
    setTUDate(null);
    setSelectedTime(null);
    setTimesArray([]);
    setMonthsOpen(false);
    setShowTimes(true);
  };

  /* =========================
     Render
     ========================= */

  const clickedClientName = `${client?.firstName || ""} ${client?.lastName || ""}`.trim();

  return (
    <div className="booking-page-full" onClick={closeTouchup}>
      {/* Close pill */}
      <div
        className="scheduled-appt-title wht abs-nav"
        onClick={(e) => {
          e.stopPropagation();
          closeTouchup();
        }}
      >
        close
      </div>

      {/* Panel */}
      <div className="booking-panel" onClick={(e) => e.stopPropagation()}>
        {/* LEFT */}
        <div className="booking-left">
          {selectedApptID && loadingDates && (
            <div className="full-blur">
              <RingLoader className="ring abs-s" color="#fff" size={30} />
            </div>
          )}

          {/* ✅ Cleaner header banner (one-line rows + range stands out) */}
          <div className="booking-header">
            <div className="booking-header-label booking-header-line">
              CURRENTLY BOOKING TOUCH UP FOR
            </div>

            <div className="booking-header-client booking-header-line">
              {clickedClientName || "CLIENT"}
            </div>

            <div className="booking-header-range booking-header-line">
              <span className="range-label">RECOMMENDED RANGE: &nbsp;</span>
              <span className="range-value">
                {recStart.format("MMM D")} – {recEnd.format("MMM D")}
              </span>
            </div>
          </div>

          {/* Appointment type selection */}
          {artist ? (
            !selectedApptID ? (
              <>
                {/* ✅ label should NOT look like a pill */}
                <div className="choose-type-label">Choose an appointment type</div>

                {artist.powderbrowTUID ? (
                  <div
                    onClick={() => setSelectedApptID(artist.powderbrowTUID)}
                    className="choose-type-option"
                    role="button"
                    tabIndex={0}
                  >
                    Brow Perfecting Session
                  </div>
                ) : null}

                {artist.lipblushTUID ? (
                  <div
                    onClick={() => setSelectedApptID(artist.lipblushTUID)}
                    className="choose-type-option"
                    role="button"
                    tabIndex={0}
                  >
                    Lip Blush Perfecting Session
                  </div>
                ) : null}

                {artist.eyelinerTUID ? (
                  <div
                    onClick={() => setSelectedApptID(artist.eyelinerTUID)}
                    className="choose-type-option"
                    role="button"
                    tabIndex={0}
                  >
                    Eyeliner Perfecting Session
                  </div>
                ) : null}
              </>
            ) : null
          ) : (
            <RingLoader className="ring abs-s" color="#fff" size={30} />
          )}

          {/* Month dropdown + jump */}
          {selectedApptID ? (
            <>
              <div className="fd-picker-row" style={{ marginTop: 6 }}>
                <div className={`fd-picker ${monthsOpen ? "open" : ""}`}>
                  <button
                    type="button"
                    className="picker-display"
                    onClick={() => setMonthsOpen((p) => !p)}
                  >
                    <span>{writtenDate}</span>
                    <i className={`fas fa-sort-down ${monthsOpen ? "rot" : ""}`} />
                  </button>

                  {monthsOpen ? (
                    <div className="abs-list" onClick={(e) => e.stopPropagation()}>
                      {upcomingMonths.map((m) => (
                        <button
                          type="button"
                          key={m.key}
                          className={
                            m.year === viewYear && m.monthIndex === viewMonthIndex
                              ? "calendar-month today"
                              : "calendar-month"
                          }
                          onClick={() => setSelectedMonthYear(m.year, m.monthIndex)}
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>

                <button
                  type="button"
                  className="refresh"
                  onClick={jumpToRecommended}
                  title="Jump to recommended range"
                >
                  <i className="fa-sharp fa-regular fa-bullseye" />
                </button>
              </div>

              <div className="booking-calendar-wrap">
                <table className="calendar-table wht">
                  <thead>
                    <tr>{weekdayshortname}</tr>
                  </thead>
                  <tbody>{calendar}</tbody>
                </table>
              </div>
            </>
          ) : null}

          {/* Times + Notes + Confirm */}
          {selectedApptID && TUDate && timesArray.length > 0 ? (
            <div style={{ marginTop: 10 }}>
              {!confirmed ? (
                <>
                  <div className="scheduled-appt-title moving-gradient c date-pill">
                    &nbsp;{`${writtenMonth} ${TUDate}, ${viewYear}`}
                  </div>

                  <div className="scheduled-appt-title gry c">Choose a time</div>

                  {/* ✅ If a time is chosen, hide other times + offer “Change time” */}
                 {showTimes ? (
  timesArray.map((time) => (
    <button
      key={time}
      type="button"
      onClick={() => {
        setSelectedTime(time);
        setShowTimes(false);
      }}
      className={
        selectedTime === time
          ? "time-pill selected"
          : "time-pill"
      }
    >
      {time}
    </button>
  ))
) : (
  <>
    <div className="time-pill selected">
      {selectedTime}
    </div>


                     <button
  type="button"
  className="change-time-link"
  onClick={() => {
    setSelectedTime(null);
    setShowTimes(true);
  }}
>
  Change time<span className="chev">›</span>
</button>

                    </>
                  )}

                  {selectedTime ? (
                    <div className="abs-list-4">
                      <textarea
                        placeholder="Add notes here..."
                        onChange={(e) => setNotes(e.currentTarget.value)}
                        className="notes-input"
                        value={notes}
                      />
                     <button
  type="button"
  onClick={createAppointment}
  className="booking-confirm"
>
  Confirm and Book
</button>

                    </div>
                  ) : (
               <button type="button" className="booking-confirm disabled" disabled>
  Confirm and Book
</button>

                  )}
                </>
              ) : (
                <div className="scheduled-appt-title moving-gradient c">Confirmed!</div>
              )}
            </div>
          ) : null}
        </div>

        {/* RIGHT */}
        <div className="booking-right">
          {selectedApptID ? (
            <>
              {/* ✅ Replace “title” with client name context */}
              <div className="scheduled-appt-title wht c">
                Booked appointments 
             
                <div className="fd-subtitle" style={{ marginTop:0 }}>
                  (Shown so you can avoid conflicts)
                </div>
              </div>

              <div className="booking-calendar-wrap">
                <table className="calendar-table wht">
                  <thead>
                    <tr>{weekdayshortname}</tr>
                  </thead>
                  <tbody>{calendarBooked}</tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="scheduled-appt-title wht c">
              Select a touch-up type to view availability + existing bookings.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
